import type { FC, ReactNode, SVGProps } from "react"
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { useQuery } from "@rocicorp/zero/react"
import {
	Link,
	useLocation,
	useMatchRoute,
	useNavigate,
} from "@tanstack/react-router"
import { AnimatePresence, motion } from "motion/react"
import {
	IconAtom2Fill18,
	IconChart3Fill18,
	IconGearFill18,
	IconLifeRingFill18,
	IconMessagesFill18,
	IconNotificationFill18,
} from "nucleo-ui-fill-18"
import {
	IconCheckOutline18 as CheckIcon,
	IconChevronDownOutline18 as ChevronDown,
	IconFlaskOutline18 as FlaskConical,
	IconXmarkOutline18 as XmarkIcon,
} from "nucleo-ui-outline-18"
// @ts-expect-error why...
import { tinykeys } from "tinykeys"
import { create } from "zustand"

import type {
	InboxTabItemId,
	SidebarBadgeStyle,
	SidebarItemId,
	SidebarSection,
} from "@productlane/lib/src/sidebar-layout"
import type { IconType } from "@productlane/ui"
import { cn } from "@productlane/lib/src/browser"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import { isSidebarItemShown } from "@productlane/lib/src/sidebar-layout"
import {
	Avatar,
	Badge,
	Button,
	Dot,
	Pill,
	Shortcut,
	Tooltip,
	useBreakpoints,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
} from "@productlane/ui/src/components/internal/dropdown"
import { queries } from "@productlane/zero/queries"

import type {
	CustomizeSidebarItems,
	CustomizeSidebarVariant,
} from "./sidebar-layout/customize-sidebar-dialog"
import { useOpenOutOfOffice } from "@/components/out-of-office/use-open-out-of-office"
import { RoleCheck } from "@/components/shared/role-check"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { useCombinedNav } from "@/lib/hooks/use-combined-nav"
import { useIsAgentPageAvailable } from "@/lib/hooks/use-is-agent-page-available"
import { useLastKnown } from "@/lib/hooks/use-last-known"
import { usePendingProjectsCount } from "@/lib/hooks/usePendingProjectsCount"
import { useLinkPress } from "@/lib/press-to-navigate"
import { useSession } from "@/lib/session"
import { NAV_SEQUENCE_TIMEOUT } from "@/lib/shortcut-sequence"
import { useCmdkState } from "@/lib/states/cmdk-state"
import { useThreadState, useTrySectionState } from "@/lib/states/global-state"
import { useMainNavState } from "@/lib/states/main-nav-state"
import { useSetupWorkspace } from "@/lib/states/setup-workspace-state"
import { useSwitchWorkspace } from "@/lib/use-switch-workspace"
import { useRole } from "@/lib/useRole"
import { sortWorkspacesByName } from "@/lib/workspace-switcher"
import { useZeroWorkspace } from "@/lib/zero"
import { toggleSupportWidget } from "@/stores/support-widget-store"
import { useAgentItems } from "./agent/use-agent-recommendations"
import { CombinedSidebar } from "./combined-sidebar"
import { useOpenProposalRowCount } from "./helpCenter/use-article-suggestions"
import { useTotalInboxThreadCount } from "./inbox/useThreadCount"
import {
	ChangelogIcon,
	CustomersIcon,
	DocsIcon,
	InboxNavIcon,
	RequestsIcon,
} from "./nav-icons"
import { isNavItemUnpublished, shouldShowTrySection } from "./nav-visibility"
import { availableNavigationShortcuts, goToLabel } from "./navigation-shortcuts"
import { NOTIFICATIONS_COPY } from "./notifications/copy"
import { mentionDotClass } from "./notifications/notification-icons"
import {
	useUnreadMentions,
	useUnreadNotifications,
} from "./notifications/use-notifications"
import { SidebarContactRow } from "./sidebar-contact-row"
import {
	HIDDEN_NAV_TOGGLE_INSET,
	MOBILE_BAR_CLEARANCE,
	MOBILE_NAV_MAX_MIDDLE,
	MOBILE_NAV_TAB,
	mobileBarPillStyles,
	mobileBarShellStyles,
	SIDEBAR_CONTENT_INSET,
	SIDEBAR_PANEL_INSET,
	SIDEBAR_RAIL_WIDTH,
	SIDEBAR_VERTICAL_INSET,
	sidebarItemStyles,
	sidebarTrackStyles,
	sidebarWorkspaceLogoStyles,
	sidebarWorkspaceRowStyles,
	uniquePhoneNavItems,
	visibleMobileNavCount,
} from "./sidebar-geometry"
import { SIDEBAR_LAYOUT_COPY } from "./sidebar-layout/copy"
import { CustomizeSidebarDialog } from "./sidebar-layout/customize-sidebar-dialog"
import { SidebarContextMenu } from "./sidebar-layout/sidebar-context-menu"
import {
	useCustomizeSidebarState,
	useSidebarSectionFoldState,
} from "./sidebar-layout/sidebar-layout-state"
import { SidebarMoreItem } from "./sidebar-layout/sidebar-more-item"
import { SidebarSectionHeader } from "./sidebar-layout/sidebar-section-header"
import { useSidebarLayout } from "./sidebar-layout/use-sidebar-layout"
import {
	AiResolutionCapBanner,
	AiResolutionCapBannerCollapsed,
} from "./threads/components/sidebar-warnings/ai-resolution-cap"
import {
	LinearReconnectRequiredBanner,
	LinearReconnectRequiredBannerCollapsed,
} from "./threads/components/sidebar-warnings/linear-revoked"
import { ViewAccessWarning } from "./threads/components/sidebar-warnings/view-access"

/** Settings rides the phone bar's expand menu instead of its own slot. */
const HIDE_ON_PHONE = "max-sm:hidden"

const docsItem: NavItem = {
	href: "/edit-docs",
	icon: DocsIcon as LucideIcon,
	title: "Docs",
	highlightFor: ["/edit-docs"],
	shortcut: ["g", "d"],
}

const featureRequestsItem: NavItem = {
	href: "/edit-requests",
	icon: RequestsIcon as LucideIcon,
	title: "Feature requests",
	highlightFor: ["/issues", "/projects"],
	shortcut: ["g", "p"],
}

const supportPortalItem: NavItem = {
	href: "/support-portal",
	icon: IconLifeRingFill18 as LucideIcon,
	title: "Support portal",
	highlightFor: ["/support-portal"],
	shortcut: ["g", "u"],
}

const changelogItem: NavItem = {
	href: "/edit-changelog",
	icon: ChangelogIcon as LucideIcon,
	title: "Changelog",
	highlightFor: ["/edit-changelog"],
	shortcut: ["g", "c"],
}

const customersItem: NavItem = {
	href: "/customers",
	icon: CustomersIcon as LucideIcon,
	title: "Customers",
	highlightFor: ["/customers", "/people", "/segments"],
	shortcut: ["g", "q"],
}

export const NAV_ITEMS: Array<NavItem> = [
	docsItem,
	featureRequestsItem,
	supportPortalItem,
	changelogItem,
	customersItem,
]

export type RightBarTab = "details" | "ai-chat"

interface ThreadSidebarState {
	isOpen: boolean
	activeTab: RightBarTab
	close: () => void
	open: () => void
	toggle: () => void
	setActiveTab: (tab: RightBarTab) => void
}

interface MainSidebarState {
	isOpen: boolean
	close: () => void
	open: () => void
}

export const useMainSidebarState = create<MainSidebarState>()((set) => ({
	isOpen: false,
	close: () => {
		set({ isOpen: false })
	},
	open: () => {
		set({ isOpen: true })
	},
}))

export const useThreadSidebarState = create<ThreadSidebarState>()(
	(set, get) => ({
		isOpen: false,
		activeTab: "details" as RightBarTab,
		close: () => {
			set({ isOpen: false })
		},
		open: () => {
			set({ isOpen: true })
		},
		toggle: () => {
			set({ isOpen: !get().isOpen })
		},
		setActiveTab: (tab: RightBarTab) => {
			set({ activeTab: tab })
		},
	}),
)

export function shouldRejectKeystrokes(
	{
		ignoreWhenFocused,
	}: {
		ignoreWhenFocused: string[]
	} = { ignoreWhenFocused: [] },
) {
	const inputs = ["input", "textarea", ...ignoreWhenFocused].map((el) =>
		el.toLowerCase(),
	)

	const activeElement = document.activeElement

	const ignoreStrokes =
		activeElement &&
		(inputs.indexOf(activeElement.tagName.toLowerCase()) !== -1 ||
			activeElement.attributes.getNamedItem("role")?.value === "textbox" ||
			activeElement.attributes.getNamedItem("contenteditable")?.value ===
				"true")

	return ignoreStrokes
}

export function useNavigationShortcuts() {
	const navigate = useNavigate()
	const { session } = useSession()
	const isAdmin = session?.userRole === "ADMIN"
	const agentPageAvailable = useIsAgentPageAvailable()
	const canOpenAgent =
		!!agentPageAvailable && session?.membershipRole !== "VIEWER"
	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
		const unsubscribe = tinykeys(
			window,
			Object.fromEntries(
				availableNavigationShortcuts({ isAdmin, canOpenAgent }).map(
					([key, { href, trackAs, replace, inboxTab }]) => [
						key,
						() => {
							if (shouldRejectKeystrokes()) {
								return
							}
							analytics.track("shortcut-navigation", { href: trackAs ?? href })
							if (inboxTab) {
								const { setInboxState, setActiveCustomViewId } =
									useThreadState.getState()
								setInboxState(inboxTab)
								setActiveCustomViewId(null)
							}
							void navigate({ href, replace })
						},
					],
				),
			),
			{ timeout: NAV_SEQUENCE_TIMEOUT },
		)
		return () => {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-call
			unsubscribe()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAdmin, canOpenAgent])
}

export function useSwitchWorkspaceShortcut() {
	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
		const unsubscribe = tinykeys(window, {
			"o w": (event: KeyboardEvent) => {
				if (shouldRejectKeystrokes()) {
					return
				}
				// cmd+k focuses its input before this keydown finishes, so the "w" would be typed into it.
				event.preventDefault()
				analytics.track("open-cmdk", { page: "Switch workspace" })
				useCmdkState.getState().openPage("Switch workspace")
			},
		})
		return () => {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-call
			unsubscribe()
		}
	}, [])
}

interface SelectWorkspaceProps {
	isOpen: boolean
	onOpenChange: (open: boolean) => void
	side?: "top" | "bottom"
	align?: "start" | "end"
	children: ReactNode
}

const WorkspaceMenuItems = memo(() => {
	const [unsortedWorkspaces] = useQuery(queries.userWorkspaces())
	const workspaces = useMemo(
		() => sortWorkspacesByName(unsortedWorkspaces),
		[unsortedWorkspaces],
	)
	const { session } = useSession()
	const { switchTo } = useSwitchWorkspace()
	const navigate = useNavigate()

	const { startSetup, setStep } = useSetupWorkspace()
	const openOutOfOffice = useOpenOutOfOffice()

	return (
		<>
			<DropdownMenuGroup>
				<DropdownMenuLabel>Workspaces</DropdownMenuLabel>
				{workspaces.map((ws) => (
					<DropdownMenuItem key={ws.id} onSelect={() => switchTo(ws.id)}>
						<div className="flex w-full min-w-0 items-center justify-between gap-2">
							<div className="flex min-w-0 items-center gap-2">
								<Avatar
									name={ws.name}
									image={ws.logoUrl}
									size="xs"
									roundness="md"
									disableColorHash
									fallbackProps={{
										className:
											"bg-gray-950 text-white dark:bg-gray-50 dark:text-gray-950",
									}}
								/>
								<div
									className={cn(
										"truncate",
										ws.id === session?.workspaceId && "font-bold",
									)}
								>
									{ws.name}
								</div>
							</div>

							{ws.id === session?.workspaceId && (
								<CheckIcon className="ml-auto h-4 w-4 flex-none" />
							)}
						</div>
					</DropdownMenuItem>
				))}
			</DropdownMenuGroup>
			<DropdownMenuSeparator />
			<DropdownMenuGroup>
				<DropdownMenuItem
					className="font-normal"
					shortcut={
						<span className="flex items-center gap-1 tracking-normal">
							<Shortcut>{ShortcutSymbols.G}</Shortcut>
							<span className="normal-case">then</span>
							<Shortcut>{ShortcutSymbols.S}</Shortcut>
						</span>
					}
					onSelect={() => {
						void navigate({ to: "/settings/general" })
					}}
				>
					Settings
				</DropdownMenuItem>
				<RoleCheck not={["VIEWER"]}>
					<DropdownMenuItem
						className="font-normal"
						onSelect={() => {
							void navigate({ to: "/settings/members" })
						}}
					>
						Manage members
					</DropdownMenuItem>
					<DropdownMenuItem
						className="font-normal"
						onSelect={() => {
							openOutOfOffice()
							analytics.track("open-out-of-office", { menu: true })
						}}
					>
						Out of office
					</DropdownMenuItem>
				</RoleCheck>
			</DropdownMenuGroup>
			<DropdownMenuSeparator />
			<DropdownMenuGroup>
				<DropdownMenuItem
					className="font-normal"
					onSelect={() => {
						startSetup()
						setStep(1)
						void navigate({ to: "/setup-workspace" })
					}}
				>
					Create or join a workspace
				</DropdownMenuItem>
				<DropdownMenuItem
					className="font-normal"
					shortcut={
						<span className="flex items-center gap-1 tracking-normal">
							<Shortcut>{ShortcutSymbols.ALT}</Shortcut>
							<Shortcut>{ShortcutSymbols.SHIFT}</Shortcut>
							<Shortcut>{ShortcutSymbols.Q}</Shortcut>
						</span>
					}
					onSelect={() => {
						void navigate({ to: "/logout" })
					}}
				>
					Log out
				</DropdownMenuItem>
			</DropdownMenuGroup>
		</>
	)
})

export const SelectWorkspace = ({
	children,
	isOpen,
	onOpenChange,
	side = "bottom",
	align = "start",
}: SelectWorkspaceProps) => {
	return (
		<DropdownMenu
			trigger={children}
			open={isOpen}
			onOpenChange={onOpenChange}
			contentProps={{
				side,
				align,
				className: "w-64 z-50",
				onClick: (e: React.MouseEvent) => {
					e.stopPropagation()
				},
			}}
		>
			<WorkspaceMenuItems />
		</DropdownMenu>
	)
}

export const Sidebar = memo(({ content }: SidebarProps) => {
	const mainSidebarState = useMainSidebarState()
	const [workspaceOpen, setWorkspaceOpen] = useState(false)
	useNavigationShortcuts()
	const combinedNav = useCombinedNav()
	const navHidden = useMainNavState((state) => state.hidden)
	const navToggleShown = combinedNav && navHidden

	useSwitchWorkspaceShortcut()

	return (
		<div className="flex flex-1 flex-col overflow-hidden bg-gray-100 sm:flex-row dark:bg-gray-950">
			<AnimatePresence>
				{mainSidebarState.isOpen && (
					<DialogPrimitive.Root
						open={mainSidebarState.isOpen}
						onOpenChange={(open) => {
							if (open) {
								mainSidebarState.open()
							} else {
								if (!workspaceOpen) {
									mainSidebarState.close()
								}
							}
						}}
					>
						<DialogPrimitive.Portal forceMount>
							<DialogPrimitive.Overlay forceMount asChild>
								<motion.div
									className="fixed inset-0 z-50 bg-black/50"
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									transition={{ duration: 0.1 }}
									exit={{ opacity: 0 }}
								/>
							</DialogPrimitive.Overlay>
							<DialogPrimitive.Content forceMount asChild>
								<motion.div
									initial={{ translateX: "-100%" }}
									animate={{ translateX: 0 }}
									transition={{
										duration: 0.2,
										ease: "easeInOut",
									}}
									exit={{ translateX: "-100%" }}
									className={cn(
										"fixed z-50 flex w-full max-w-[16rem] flex-1 flex-col",
										SIDEBAR_PANEL_INSET,
									)}
								>
									<div className="flex flex-1 select-none flex-col items-start rounded-r-2xl bg-white pr-2 shadow-xl ring-1 ring-black/10 dark:bg-gray-900 dark:shadow-black dark:ring-white/15">
										<SidebarContent
											workspaceOpen={workspaceOpen}
											setWorkspaceOpen={setWorkspaceOpen}
											onNavClick={() => mainSidebarState.close()}
										/>
									</div>
								</motion.div>
							</DialogPrimitive.Content>
						</DialogPrimitive.Portal>
					</DialogPrimitive.Root>
				)}
			</AnimatePresence>

			{combinedNav ? <CombinedSidebar /> : <WideDesktopSidebar />}

			{content && (
				<div
					className={cn(
						"flex h-dvh w-full min-w-0",
						SIDEBAR_CONTENT_INSET,
						MOBILE_BAR_CLEARANCE,
					)}
				>
					<div
						className={cn(
							"relative flex h-full flex-1 grow flex-col overflow-hidden bg-white sm:rounded-2xl sm:shadow-md sm:ring-1 sm:ring-black/10 dark:bg-gray-900 dark:shadow-black dark:ring-white/15",
							navToggleShown && HIDDEN_NAV_TOGGLE_INSET,
						)}
					>
						{content}
					</div>
				</div>
			)}
		</div>
	)
})

export interface NavItem {
	href: string
	title: string
	icon: LucideIcon | IconType
	highlightFor?: string[]
	exact?: boolean
	shortcut?: Array<string>
	className?: string
	inboxCounter?: boolean
	experimental?: boolean
	count?: number
	/** Replaces the number, for counts that are capped at the source ("9+"). */
	countLabel?: string
	badgeStyle?: SidebarBadgeStyle
	keywords?: string
	iconClassName?: string
}

interface SidebarProps {
	content: ReactNode
}

const inboxRoutes = ["/threads"]

const navLinkBaseStyles = sidebarItemStyles(false)
const navLinkCollapsedStyles = sidebarItemStyles(true)
const navIconCollapsedStyles =
	"flex h-4 w-4 shrink-0 items-center justify-center text-primary"
const navIconExpandedStyles =
	"mr-3 flex h-4 w-4 shrink-0 items-center justify-center text-primary"

const useActiveItem = ({
	highlightFor,
	path,
	end = true,
}: {
	highlightFor?: string[]
	path: string
	end?: boolean
}) => {
	const { pathname } = useLocation()
	const matchRoute = useMatchRoute()
	const match = matchRoute({ to: path, fuzzy: !end })

	const matchesHighlight = highlightFor
		? highlightFor.some((prefix) => pathname.startsWith(prefix))
		: false

	return !!match || matchesHighlight
}

const TotalInboxCounterTag = memo(() => {
	const { label, count } = useTotalInboxThreadCount()

	if (count > 0) {
		return (
			<Badge variant="nav-count" className="bg-gray-200/80 dark:bg-gray-800">
				{label}
			</Badge>
		)
	} else {
		return null
	}
})

const TotalInboxCounterTagSmall = memo(() => {
	const { label, count } = useTotalInboxThreadCount()

	if (count > 0) {
		return (
			<Badge
				variant="nav-count"
				size="xs"
				className="h-4 min-w-4 bg-gray-300 px-0.5 text-[11px] font-bold text-gray-900 dark:bg-gray-600 dark:text-gray-100"
			>
				{label}
			</Badge>
		)
	} else {
		return null
	}
})

export const NavItem = memo(
	(props: NavItem & { collapsed?: boolean; onNavClick?: () => void }) => {
		const Icon = useMemo(() => {
			return props.icon
		}, [props.icon])

		const isActive = useActiveItem({
			path: props.href,
			end: props.exact,
			highlightFor: props.highlightFor,
		})
		const linkPress = useLinkPress(props.href, props.onNavClick)

		const content = (
			<Link
				className={cn(
					isActive
						? props.collapsed
							? "bg-gray-200 dark:bg-gray-800"
							: "bg-gray-150 dark:bg-gray-850"
						: "bg-transparent hover:bg-gray-150 hover:dark:bg-gray-800",
					props.collapsed ? navLinkCollapsedStyles : navLinkBaseStyles,
					!props.collapsed && "w-full",
					props.className,
				)}
				{...linkPress}
				to={props.href}
			>
				<Icon
					strokeWidth={1.75}
					className={cn(
						props.collapsed ? navIconCollapsedStyles : navIconExpandedStyles,
						props.iconClassName,
					)}
				/>
				{props.collapsed && (
					<div className="absolute right-0 top-0 flex">
						{props.experimental && <ExperimentalTag />}
						{props.count !== undefined &&
							(props.badgeStyle ?? "count") === "count" && (
								<CountTagSmall count={props.count} label={props.countLabel} />
							)}
						{props.count !== undefined && props.badgeStyle === "dot" && (
							<CountDot count={props.count} className="h-2.5 w-2.5" />
						)}
						{props.inboxCounter && <TotalInboxCounterTagSmall />}
					</div>
				)}
				{!props.collapsed && (
					<>
						<div
							className={cn(
								"min-w-0 flex-1 select-none truncate text-left text-sm font-medium tracking-tight text-primary",
							)}
						>
							{props.title}
						</div>

						<div className="flex flex-none gap-1 pl-1.5">
							{props.experimental && <ExperimentalTag />}
							{props.count !== undefined &&
								(props.badgeStyle ?? "count") === "count" && (
									<CountTag count={props.count} label={props.countLabel} />
								)}
							{props.count !== undefined && props.badgeStyle === "dot" && (
								<CountDot count={props.count} className="h-5 w-5" />
							)}
							{props.inboxCounter && <TotalInboxCounterTag />}
						</div>
					</>
				)}
			</Link>
		)

		if (props.collapsed) {
			return (
				<Tooltip side="right" label={props.title}>
					{content}
				</Tooltip>
			)
		}

		if (props.shortcut) {
			return (
				<Tooltip
					side="right"
					label={
						<NavShortcutLabel title={props.title} shortcut={props.shortcut} />
					}
				>
					{content}
				</Tooltip>
			)
		}
		return content
	},
)

/** "Go to docs  G then D", like Linear's nav tooltips. */
export const NavShortcutLabel = memo(
	({ title, shortcut }: { title: string; shortcut: Array<string> }) => (
		<div className="flex items-center gap-2">
			<span>{goToLabel(title)}</span>
			<span className="flex items-center gap-1">
				{shortcut[0] && <Shortcut>{shortcut[0]}</Shortcut>}
				{shortcut.length > 1 && <span className="text-xs">then</span>}
				{shortcut[1] && <Shortcut>{shortcut[1]}</Shortcut>}
			</span>
		</div>
	),
)

const CountTag = memo(({ count, label }: { count: number; label?: string }) => {
	if (count > 0) {
		return (
			<Badge variant="nav-count" className="bg-gray-200/80 dark:bg-gray-800">
				{label ?? (count > 99 ? "99+" : count)}
			</Badge>
		)
	} else {
		return null
	}
})

const CountTagSmall = memo(
	({ count, label }: { count: number; label?: string }) => {
		if (count > 0) {
			return (
				<Badge
					variant="nav-count"
					size="xs"
					className="h-4 min-w-4 bg-gray-300 px-0.5 text-[11px] font-bold text-gray-900 dark:bg-gray-600 dark:text-gray-100"
				>
					{label ?? (count > 99 ? "99+" : count)}
				</Badge>
			)
		} else {
			return null
		}
	},
)

export const CountDot = memo(
	({ count, className }: { count: number; className: string }) =>
		count > 0 ? (
			<span className={cn("flex items-center justify-center", className)}>
				<Dot size="sm" color="gray" />
			</span>
		) : null,
)

const notificationRoutes = ["/notifications"]

const NotificationsNavItem = memo(
	(props: {
		collapsed?: boolean
		onNavClick?: () => void
		className?: string
	}) => {
		const { count, formattedCount } = useUnreadNotifications()
		const { count: unreadMentions } = useUnreadMentions()
		return (
			<NavItem
				href="/notifications"
				icon={IconNotificationFill18 as LucideIcon}
				iconClassName={mentionDotClass(unreadMentions)}
				title={NOTIFICATIONS_COPY.navLabel}
				shortcut={["g", "n"]}
				highlightFor={notificationRoutes}
				count={count}
				countLabel={formattedCount}
				{...props}
			/>
		)
	},
)

export const ExperimentalTag = memo(() => {
	return (
		<Tooltip label="Experimental feature" enabled side="right">
			<Pill
				variant="green"
				className="select-none px-4 py-1 text-xxs tracking-wider"
				size="sm"
			>
				<FlaskConical />
			</Pill>
		</Tooltip>
	)
})

export const WideDesktopSidebar = memo(() => {
	const [isHovered, setIsHovered] = useState(false)
	const { isMobile } = useBreakpoints()
	const timeoutRef = useRef<NodeJS.Timeout | null>(null)

	const handleMouseEnter = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
			timeoutRef.current = null
		}
		timeoutRef.current = setTimeout(() => {
			setIsHovered(true)
			timeoutRef.current = null
		}, 300)
	}, [])

	const handleMouseLeave = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
			timeoutRef.current = null
		}
		setIsHovered(false)
	}, [])

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}
		}
	}, [])

	return (
		<>
			<div
				className={cn(
					"first-run-sidebar group relative flex h-full shrink-0 select-none",
					SIDEBAR_VERTICAL_INSET,
					mobileBarShellStyles,
				)}
				onMouseEnter={isMobile ? undefined : handleMouseEnter}
				onMouseLeave={isMobile ? undefined : handleMouseLeave}
			>
				<div
					className={cn(
						"flex h-full shrink-0 flex-col items-start overflow-hidden bg-gray-100 dark:bg-gray-950",
						SIDEBAR_RAIL_WIDTH,
						mobileBarPillStyles,
					)}
				>
					<SidebarContent collapsed={true} />
				</div>
				<AnimatePresence>
					{!isMobile && isHovered && (
						<motion.div
							initial={{ width: "2.75rem", opacity: 0.8 }}
							animate={{ width: "12.25rem", opacity: 1 }}
							exit={{ width: "2.75rem", opacity: 0 }}
							transition={{
								width: {
									duration: 0.2,
									ease: [0.25, 0.1, 0.25, 1],
								},
								opacity: { duration: 0.15 },
							}}
							// inset-y-1 matches the wrapper's py-1: absolute insets resolve
							// against the padding box, so the rail's padding does not apply here.
							className={cn(
								"absolute left-0 z-50 flex flex-col items-start rounded-r-2xl bg-white shadow-xl ring-1 ring-black/10 dark:bg-gray-900 dark:shadow-black dark:ring-white/15",
								SIDEBAR_PANEL_INSET,
							)}
							style={{ willChange: "width", pointerEvents: "auto" }}
							onMouseEnter={handleMouseEnter}
							onMouseLeave={handleMouseLeave}
						>
							<div className="flex h-full w-[12.25rem] flex-col">
								<SidebarContent collapsed={false} />
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
			{/* Outside the rail's root: portal events bubble through the React tree and would open the hover panel. */}
			<CustomizeSidebarHost />
		</>
	)
})

const agentItem: NavItem = {
	href: "/agent",
	icon: IconAtom2Fill18 as LucideIcon,
	title: "Agent",
	highlightFor: ["/agent"],
	shortcut: ["g", "a"],
}

const recommendationsItem: NavItem = {
	href: "/agent",
	icon: IconAtom2Fill18 as LucideIcon,
	title: "Agent",
}

const reportingItem: NavItem = {
	href: "/reporting",
	icon: IconChart3Fill18 as LucideIcon,
	title: "Reporting",
	highlightFor: ["/reporting"],
	shortcut: ["g", "r"],
}

const SIDEBAR_NAV_ITEMS: Record<
	Exclude<SidebarItemId, InboxTabItemId>,
	NavItem
> = {
	notifications: {
		href: "/notifications",
		icon: IconNotificationFill18 as LucideIcon,
		title: NOTIFICATIONS_COPY.navLabel,
		shortcut: ["g", "n"],
		highlightFor: notificationRoutes,
	},
	inbox: {
		href: "/threads",
		icon: InboxNavIcon as LucideIcon,
		title: "Inbox",
		shortcut: ["g", "i"],
		highlightFor: inboxRoutes,
	},
	agent: agentItem,
	recommendations: recommendationsItem,
	reporting: reportingItem,
	docs: docsItem,
	"feature-requests": featureRequestsItem,
	"support-portal": supportPortalItem,
	changelog: changelogItem,
	customers: customersItem,
}

export interface SidebarEntry {
	id: SidebarItemId
	item: NavItem
	shown: boolean
	unpublished: boolean
}

const SIDEBAR_ITEM_PHONE_ORDER: Partial<Record<SidebarItemId, string>> = {
	notifications: "max-sm:order-2",
	inbox: "max-sm:order-1",
	// Wider screens reach the agent through its own sidebar section.
	agent: "max-sm:order-1 sm:hidden",
	// On phones the Agent row above opens the same list.
	recommendations: "max-sm:hidden",
}

/** Items the combined sidebar adds (the inbox tabs) and the ones it drops (the single Inbox row). */
export interface SidebarItemOverrides {
	items?: Partial<Record<SidebarItemId, NavItem>>
	omit?: readonly SidebarItemId[]
}

export function useAvailableSidebarItems(overrides?: SidebarItemOverrides) {
	const { session } = useSession()
	const agentPageAvailable = useIsAgentPageAvailable()
	const isViewer = session?.membershipRole === "VIEWER"
	return useMemo(() => {
		const items: Partial<Record<SidebarItemId, NavItem>> = {
			...SIDEBAR_NAV_ITEMS,
			...overrides?.items,
		}
		for (const id of overrides?.omit ?? []) delete items[id]
		if (isViewer) delete items.reporting
		if (isViewer || !agentPageAvailable) {
			delete items.agent
			delete items.recommendations
		}
		return items
	}, [isViewer, agentPageAvailable, overrides])
}

export const CustomizeSidebarHost = memo(
	({
		overrides,
		variant,
		icons,
		iconClassNames,
	}: {
		overrides?: SidebarItemOverrides
		variant?: CustomizeSidebarVariant
		/** The sidebar's own glyphs, so each row in the dialog looks like its row in the nav. */
		icons?: Partial<Record<SidebarItemId, IconType>>
		iconClassNames?: Partial<Record<SidebarItemId, string>>
	}) => {
		const { isOpen, setOpen } = useCustomizeSidebarState()
		const available = useAvailableSidebarItems(overrides)
		const { layout, save } = useSidebarLayout()
		const items = useMemo(
			() =>
				Object.fromEntries(
					Object.entries(available).map(([id, item]) => [
						id,
						{
							title: item.title,
							icon: icons?.[id as SidebarItemId] ?? (item.icon as LucideIcon),
							iconClassName: iconClassNames?.[id as SidebarItemId],
						},
					]),
				) as CustomizeSidebarItems,
			[available, icons, iconClassNames],
		)
		return (
			<CustomizeSidebarDialog
				isOpen={isOpen}
				onOpenChange={setOpen}
				items={items}
				layout={layout}
				onChange={(next) => void save(next)}
				variant={variant}
			/>
		)
	},
)

const CollapsedLogoWithBadge = memo(
	({
		logoUrl,
		workspaceName,
	}: {
		logoUrl: string | null | undefined
		workspaceName: string | undefined
	}) => {
		return (
			<div className={sidebarWorkspaceLogoStyles}>
				<Avatar
					image={logoUrl}
					name={workspaceName || "Productlane"}
					size="s"
					roundness="md"
					disableColorHash
					fallbackProps={{
						className:
							"bg-gray-950 text-white dark:bg-gray-50 dark:text-gray-950",
					}}
				/>
			</div>
		)
	},
)

const TrySection = memo(
	({
		items,
		collapsed = false,
		onNavClick,
	}: {
		items: Array<NavItem>
		collapsed?: boolean
		onNavClick?: () => void
	}) => {
		const { dismissed, dismiss } = useTrySectionState()

		if (
			!shouldShowTrySection({
				collapsed,
				dismissed,
				unpublishedCount: items.length,
			})
		) {
			return null
		}

		return (
			<motion.div
				initial={{ opacity: 0, y: 4 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
				className={cn("group/try mt-5", sidebarTrackStyles)}
			>
				<div className="flex h-5 items-center justify-between pl-2 pr-1.5">
					<span className="select-none text-xs font-medium tracking-wide text-tertiary">
						Try
					</span>
					<button
						type="button"
						onClick={dismiss}
						aria-label="Dismiss"
						className="flex h-4 w-4 items-center justify-center rounded text-tertiary opacity-0 transition-opacity hover:text-secondary group-hover/try:opacity-100"
					>
						<XmarkIcon strokeWidth={1.75} className="h-3.5 w-3.5" />
					</button>
				</div>
				{items.map((item) => (
					<NavItem key={item.title} {...item} onNavClick={onNavClick} />
				))}
			</motion.div>
		)
	},
)

/** The two-line mark the marketing nav uses, crossing into an X once open. */
const MobileMenuLines = memo(({ open }: { open: boolean }) => (
	<span aria-hidden className="relative block h-5 w-5">
		<span
			className={cn(
				"ease-[cubic-bezier(0.23,1,0.32,1)] absolute left-0 top-1/2 block h-[1.5px] w-[18px] rounded-full bg-current transition-transform duration-200",
				open ? "rotate-45" : "-translate-y-[3px]",
			)}
		/>
		<span
			className={cn(
				"ease-[cubic-bezier(0.23,1,0.32,1)] absolute left-0 top-1/2 block h-[1.5px] w-[18px] rounded-full bg-current transition-transform duration-200",
				open ? "-rotate-45" : "translate-y-[3px]",
			)}
		/>
	</span>
))

/**
 * The phone bar carries the nav in sidebar order and hands whatever runs past
 * the edge to the expand menu, so a workspace with more published features
 * loses nothing off the end.
 */
const MobileNavBar = memo(
	({
		items,
		hiddenItems = [],
		onNavClick,
	}: {
		items: NavItem[]
		hiddenItems?: NavItem[]
		onNavClick?: () => void
	}) => {
		const navigate = useNavigate()
		const trackRef = useRef<HTMLDivElement>(null)
		const [barWidth, setBarWidth] = useState(0)
		const [expandOpen, setExpandOpen] = useState(false)

		useEffect(() => {
			const node = trackRef.current
			if (!node) return
			const observer = new ResizeObserver(([entry]) => {
				setBarWidth(entry?.contentRect.width ?? 0)
			})
			observer.observe(node)
			return () => {
				observer.disconnect()
			}
		}, [])

		const visible = Math.min(
			visibleMobileNavCount(barWidth, items.length),
			MOBILE_NAV_MAX_MIDDLE,
		)
		const inline = items.slice(0, visible)
		const overflow = [...items.slice(visible), ...hiddenItems]

		return (
			<div ref={trackRef} className="contents" translate="no">
				<NavItem
					href="/threads"
					icon={InboxNavIcon as LucideIcon}
					title="Inbox"
					shortcut={["g", "i"]}
					highlightFor={inboxRoutes}
					inboxCounter={true}
					collapsed={true}
					onNavClick={onNavClick}
					className={MOBILE_NAV_TAB}
				/>
				{inline.map((item) => (
					<NavItem
						key={item.title}
						{...item}
						collapsed={true}
						onNavClick={onNavClick}
						className={MOBILE_NAV_TAB}
					/>
				))}
				<NotificationsNavItem
					collapsed={true}
					onNavClick={onNavClick}
					className={MOBILE_NAV_TAB}
				/>
				<DropdownMenu
					trigger={
						<button
							type="button"
							aria-label="More navigation"
							className={cn(
								"flex cursor-default items-center text-primary hover:bg-gray-150 hover:dark:bg-gray-800",
								MOBILE_NAV_TAB,
							)}
						>
							<MobileMenuLines open={expandOpen} />
						</button>
					}
					open={expandOpen}
					onOpenChange={setExpandOpen}
					contentProps={{ side: "top", align: "end", className: "w-56 z-50" }}
				>
					{overflow.length > 0 && (
						<>
							<DropdownMenuGroup>
								{overflow.map((item) => (
									<DropdownMenuItem
										key={item.title}
										Icon={item.icon as LucideIcon}
										onSelect={() => {
											onNavClick?.()
											void navigate({ href: item.href })
										}}
									>
										{item.title}
									</DropdownMenuItem>
								))}
							</DropdownMenuGroup>
							<DropdownMenuSeparator />
						</>
					)}
					<DropdownMenuGroup>
						<DropdownMenuItem
							Icon={IconMessagesFill18 as LucideIcon}
							onSelect={() => {
								onNavClick?.()
								toggleSupportWidget()
							}}
						>
							Contact us
						</DropdownMenuItem>
					</DropdownMenuGroup>
					<DropdownMenuSeparator />
					<WorkspaceMenuItems />
				</DropdownMenu>
			</div>
		)
	},
)

/** The customized split of nav items: shown per section, hidden (for More), and unpublished. */
export function useSidebarEntries(overrides?: SidebarItemOverrides) {
	const workspace = useZeroWorkspace()
	const openProposalCount = useOpenProposalRowCount()
	const pendingProjectsCount = usePendingProjectsCount()
	const unreadNotifications = useUnreadNotifications()
	const inboxCount = useTotalInboxThreadCount()
	const { actionable: openRecommendations } = useAgentItems()
	const available = useAvailableSidebarItems(overrides)
	const { layout } = useSidebarLayout()

	// Last session's split stands in until the workspace row loads, so items
	// don't drop from the list into Try a moment after first paint.
	const unpublishedTitles = useLastKnown(
		"nav-unpublished",
		workspace
			? NAV_ITEMS.filter((item) =>
					isNavItemUnpublished(item.title, workspace),
				).map((item) => item.title)
			: undefined,
		[],
	)

	const counts: Partial<
		Record<SidebarItemId, Pick<NavItem, "count" | "countLabel">>
	> = {
		notifications: {
			count: unreadNotifications.count,
			countLabel: unreadNotifications.formattedCount,
		},
		inbox: { count: inboxCount.count, countLabel: inboxCount.label },
		recommendations: { count: openRecommendations },
		docs: { count: openProposalCount },
		"feature-requests": workspace?.domainPublic
			? { count: pendingProjectsCount }
			: {},
	}

	const entriesFor = (section: SidebarSection): SidebarEntry[] =>
		layout.order[section].flatMap((id) => {
			const base = available[id]
			if (!base) return []
			const item = { ...base, ...counts[id] }
			return [
				{
					id,
					item,
					shown: isSidebarItemShown(layout.visibility[id], item.count),
					unpublished: unpublishedTitles.includes(item.title),
				},
			]
		})
	const personalEntries = entriesFor("personal")
	const inboxEntries = entriesFor("inbox")
	const workspaceEntries = entriesFor("workspace")
	const personalShown = personalEntries.filter((entry) => entry.shown)
	const workspaceShown = workspaceEntries.filter(
		(entry) => entry.shown && !entry.unpublished,
	)
	const inboxShown = inboxEntries.filter((entry) => entry.shown)
	const hiddenEntries = [
		...personalEntries,
		...inboxEntries,
		...workspaceEntries,
	].filter((entry) => !entry.shown && !entry.unpublished)
	const hiddenTryItems = workspaceEntries
		.filter(
			(entry) => entry.unpublished && layout.visibility[entry.id] !== "hidden",
		)
		.map((entry) => entry.item)

	const moreEntries = hiddenEntries.map(({ item }) => ({
		href: item.href,
		title: item.title,
		icon: item.icon as LucideIcon,
		highlightFor: item.highlightFor,
	}))

	return {
		layout,
		personalEntries,
		personalShown,
		inboxShown,
		workspaceEntries,
		workspaceShown,
		hiddenEntries,
		hiddenTryItems,
		moreEntries,
	}
}

/** Slash focuses whichever search the current screen owns, so settings keeps the user in place. */
export function useFocusSearchShortcut() {
	const navigate = useNavigate()
	const { pathname } = useLocation()
	const focusSearch = useCallback(() => {
		if (pathname.startsWith("/settings")) {
			window.dispatchEvent(new CustomEvent("focus-settings-search"))
		} else if (pathname.startsWith("/threads")) {
			window.dispatchEvent(new CustomEvent("focus-inbox-search"))
		} else {
			void navigate({ href: "/threads?focus=search" })
		}
	}, [navigate, pathname])

	useShortcut("Slash", focusSearch, { preventDefault: true })
	useShortcut("shift + 7", focusSearch, { preventDefault: true })
	return focusSearch
}

const SidebarContent = memo(
	({
		collapsed = false,
		workspaceOpen,
		setWorkspaceOpen,
		onNavClick,
	}: {
		collapsed?: boolean
		workspaceOpen?: boolean
		setWorkspaceOpen?: (open: boolean) => void
		onNavClick?: () => void
	}) => {
		const workspace = useZeroWorkspace()
		const { session } = useSession()
		const { role } = useRole()
		const { isMobile } = useBreakpoints()
		const {
			layout,
			personalShown,
			workspaceEntries,
			workspaceShown,
			hiddenEntries,
			hiddenTryItems,
			moreEntries,
		} = useSidebarEntries()
		const workspaceFolded = useSidebarSectionFoldState(
			(state) => !!state.folded.workspace,
		)
		const toggleSectionFold = useSidebarSectionFoldState(
			(state) => state.toggle,
		)
		const [internalWorkspaceOpen, setInternalWorkspaceOpen] = useState(false)
		const isWorkspaceOpen = workspaceOpen ?? internalWorkspaceOpen
		const setWorkspaceOpenState = useCallback(
			(open: boolean) => {
				if (setWorkspaceOpen) {
					setWorkspaceOpen(open)
				} else {
					setInternalWorkspaceOpen(open)
				}
			},
			[setWorkspaceOpen],
		)

		const renderEntry = ({ id, item }: SidebarEntry) => (
			<NavItem
				key={id}
				{...item}
				badgeStyle={layout.badgeStyle}
				collapsed={collapsed}
				onNavClick={onNavClick}
				className={SIDEBAR_ITEM_PHONE_ORDER[id]}
			/>
		)

		useFocusSearchShortcut()

		if (isMobile) {
			// Inbox and Notifications keep their own slots in the phone bar.
			const toPhoneItems = (entries: SidebarEntry[]) =>
				entries
					.filter(
						(entry) => entry.id !== "inbox" && entry.id !== "notifications",
					)
					.map((entry) => ({ ...entry.item, badgeStyle: layout.badgeStyle }))
			const phone = uniquePhoneNavItems(
				toPhoneItems([...personalShown, ...workspaceShown]),
				toPhoneItems(hiddenEntries),
			)
			return (
				<MobileNavBar
					items={phone.shown}
					hiddenItems={phone.hidden}
					onNavClick={onNavClick}
				/>
			)
		}

		return (
			<SidebarContextMenu hiddenItems={moreEntries} onNavClick={onNavClick}>
				<div
					className="flex h-full w-full flex-col max-sm:contents"
					translate="no"
				>
					<div
						className={cn(
							"flex w-full select-none items-center pb-3 pt-2",
							collapsed ? "justify-center px-0" : "px-1.5",
							"max-sm:order-5 max-sm:w-auto max-sm:p-0",
						)}
					>
						{collapsed ? (
							<CollapsedLogoWithBadge
								logoUrl={workspace?.logoUrl ?? session?.workspaceLogoUrl}
								workspaceName={workspace?.name ?? session?.workspaceName}
							/>
						) : (
							<>
								<SelectWorkspace
									isOpen={isWorkspaceOpen}
									onOpenChange={(open) => setWorkspaceOpenState(open)}
								>
									<Button
										variant="ghost"
										className={cn(
											"text-primary hover:bg-gray-150 hover:dark:bg-gray-800",
											sidebarWorkspaceRowStyles,
										)}
										onClick={(e) => {
											e.stopPropagation()
										}}
										wrapperClassName="flex w-full"
									>
										<div className="flex w-full min-w-0 items-center gap-2">
											<Avatar
												image={workspace?.logoUrl ?? session?.workspaceLogoUrl}
												name={
													workspace?.name ||
													session?.workspaceName ||
													"Productlane"
												}
												size="s"
												roundness="md"
												disableColorHash
												fallbackProps={{
													className:
														"bg-gray-950 text-white dark:bg-gray-50 dark:text-gray-950",
												}}
											/>

											<div className="min-w-0 flex-1 truncate text-left text-[0.9rem] font-semibold">
												{workspace?.name ?? session?.workspaceName}
											</div>
											<ChevronDown
												strokeWidth={1.75}
												className="ml-auto h-3 w-3 flex-none text-secondary"
											/>
										</div>
									</Button>
								</SelectWorkspace>
							</>
						)}
					</div>

					<div
						className={cn("mt-2", sidebarTrackStyles, "max-sm:contents", {
							"mt-0": role === "VIEWER",
						})}
					>
						{personalShown.map(renderEntry)}
					</div>

					{workspaceEntries.length > 0 && (
						<div className={cn("mt-3", sidebarTrackStyles, "max-sm:contents")}>
							<SidebarSectionHeader
								label={SIDEBAR_LAYOUT_COPY.sections.workspace}
								collapsed={collapsed}
								folded={workspaceFolded}
								onToggle={() => toggleSectionFold("workspace")}
							/>
							{!workspaceFolded && workspaceShown.map(renderEntry)}
							{!workspaceFolded && hiddenEntries.length > 0 && (
								<SidebarMoreItem
									items={moreEntries}
									collapsed={collapsed}
									onNavClick={onNavClick}
								/>
							)}
						</div>
					)}

					<TrySection
						items={hiddenTryItems}
						collapsed={collapsed}
						onNavClick={onNavClick}
					/>

					<div className="flex-1 max-sm:hidden" />

					{collapsed && (
						<div className="max-sm:hidden">
							<LinearReconnectRequiredBannerCollapsed />
							<AiResolutionCapBannerCollapsed />
						</div>
					)}

					{!collapsed && (
						<>
							<LinearReconnectRequiredBanner />
							<AiResolutionCapBanner />
							<ViewAccessWarning />
						</>
					)}

					<div className={cn(sidebarTrackStyles, "pb-1.5", "max-sm:contents")}>
						<NavItem
							href="/settings/general"
							icon={IconGearFill18 as LucideIcon}
							title="Settings"
							shortcut={["g", "s"]}
							highlightFor={["/settings"]}
							collapsed={collapsed}
							onNavClick={onNavClick}
							className={HIDE_ON_PHONE}
						/>
					</div>

					<SidebarContactRow collapsed={collapsed} />
				</div>
			</SidebarContextMenu>
		)
	},
)

type LucideIcon = FC<SVGProps<SVGSVGElement>>
